import {
    BillingRun,
    BillingRunParams,
    InvoiceConfig,
    ProjectCheckConfig,
} from '../types';
import { supabase } from './supabaseClient';
import { loadProjectsFromCloud } from './cloudService';
import { getNormalizedServices, isChildProject } from './projectLv';
import {
    buildProjectServiceLines,
    createFinalInvoice,
    createInvoiceDraft,
    fetchCustomerTerms,
    fetchExternalAdvances,
    fetchProjectInvoices,
    fetchReservations,
    reservationsForProject,
} from './invoiceService';
import { invoicedQuantityByService } from './advanceInvoiceLogic';
import { fetchLastUsedInvoiceDefaults } from './invoiceCostCenters';
import {
    LastUsedInvoiceDefaults,
    performedDetails,
    servicePeriodFromSelection,
} from './invoiceLogic';
import {
    establishedBillingMode,
    projectUncoveredPriorBilling,
    resolveBillingMode,
} from './billingMode';
import { buildProjectCheckInput } from './projectCheckService';
import { runProjectCheck } from './projectCheckLogic';
import {
    BillingCandidate,
    BillingRunPreview,
    deliveryPeriodForGroup,
    deliveryPeriodNote,
    previewBillingRun,
} from './billingRunLogic';
import { getServiceLog } from './serviceJournal';

/**
 * Ausfuehrung des Rechnungslaufs.
 *
 * Die Auswahl- und Gruppierungslogik steht vollstaendig in billingRunLogic und
 * ist dort ohne Datenbank getestet. Hier steht nur das Beschaffen und das
 * Schreiben.
 */

const mapRunRow = (row: any): BillingRun => ({
    id: row.id,
    status: row.status,
    params: row.params ?? {},
    createdInvoiceIds: Array.isArray(row.created_invoice_ids) ? row.created_invoice_ids : [],
    report: row.report ?? {},
    createdAt: row.created_at,
    createdBy: row.created_by,
});

/**
 * Der Name, unter dem der Lauf den Kunden eines Projekts zeigt.
 *
 * Die Projektliste liefert keinen Kundennamen: `customer_name` ist keine
 * Spalte von PROJECT_COLUMNS_LIST. Bis zum 22.10.2026 stand hier
 * `p.customer_name` — immer undefined, und der Assistent zeigte deshalb die
 * Kunden-ID statt des Namens.
 *
 * Massgeblich ist der Kundenstamm: die Gruppe IST der Debitor, und dessen Namen
 * traegt auch der Beleg. Die Zuordnung reicht der Aufrufer herein (die
 * Belegliste hat die Kunden ohnehin geladen) — der Lauf fragt also weder je
 * Projekt noch ein zweites Mal nach.
 *
 * Rueckfall wie in cockpitEngine: der Auftraggeber aus den Metadaten. Er greift
 * fuer einen Kunden, den die Zuordnung nicht kennt — etwa einen archivierten,
 * den fetchCustomers ausblendet. Bewusst NICHT `client_name`: das ist der
 * Bauherr, nicht der Rechnungsempfaenger. Fehlt beides, bleibt es undefined,
 * und die Oberflaeche zeigt wie bisher die ID.
 */
export const billingRunCustomerName = (
    customerId: string | null | undefined,
    metadataCustomerName: unknown,
    customerNames?: ReadonlyMap<string, string>,
): string | undefined => {
    const fromCrm = customerId ? customerNames?.get(customerId)?.trim() : undefined;
    if (fromCrm) return fromCrm;
    // Die Metadaten sind Freitext aus dem Bericht — kein Versprechen auf einen
    // String, daher die Pruefung statt einer Typbehauptung.
    const fromMeta = typeof metadataCustomerName === 'string' ? metadataCustomerName.trim() : '';
    return fromMeta || undefined;
};

/**
 * Sammelt die Kandidaten und prueft jedes Projekt.
 *
 * Bewusst sequenziell: jedes Projekt zieht Berichtsarchiv und Reservierungen.
 * Bei einer Auswahl von dreissig Projekten waeren dreissig gleichzeitige
 * Abfragebuendel eine unnoetige Last, und spuerbar schneller waere der Lauf
 * dadurch nicht — der Nutzer wartet ohnehin auf die Vorschau.
 *
 * `customerNames`: Kunden-ID → Name aus dem Kundenstamm, siehe
 * billingRunCustomerName. Ohne sie faellt der Name auf die Metadaten zurueck.
 */
export const gatherCandidates = async (
    params: BillingRunParams,
    checkConfig?: ProjectCheckConfig,
    todayIso?: string,
    onProgress?: (done: number, total: number) => void,
    customerNames?: ReadonlyMap<string, string>,
): Promise<BillingCandidate[]> => {
    const today = todayIso ?? new Date().toISOString().slice(0, 10);
    const all = await loadProjectsFromCloud();

    let selected = all;
    if (params.projectIds?.length) {
        const ids = new Set(params.projectIds);
        selected = selected.filter((p) => ids.has(p.id));
    } else if (params.customerIds?.length) {
        const ids = new Set(params.customerIds);
        selected = selected.filter((p) => ids.has(p.customer_id));
    }
    if (params.onlyProjectStatus?.length) {
        // Set<string>: die Spalte `status` ist Freitext (ListProject), der
        // Vergleich bleibt derselbe.
        const wanted = new Set<string>(params.onlyProjectStatus);
        selected = selected.filter((p) =>
            wanted.has(p.status ?? p.data?.metadata?.globalData?.status),
        );
    }

    const out: BillingCandidate[] = [];
    let done = 0;
    for (const p of selected) {
        const base: BillingCandidate = {
            projectId: p.id,
            projectNumber: p.project_number,
            projectName: p.name,
            customerId: p.customer_id ?? undefined,
            // Aus dem Kundenstamm, nicht aus der Liste — siehe
            // billingRunCustomerName.
            customerName: billingRunCustomerName(
                p.customer_id,
                p.data?.metadata?.customerName,
                customerNames,
            ),
            services: getNormalizedServices(p),
            serviceLog: getServiceLog(p.data),
            isChild: isChildProject(p),
            // Der vertraglich vereinbarte Sicherheitseinbehalt steht am
            // Projekt und ist Vorbelegung fuer dessen Belege — im Lauf ist er
            // die EINZIGE Quelle, denn bei mehreren Projekten auf einem Beleg
            // gibt es kein primaeres Projekt, aus dem createInvoiceDraft ihn
            // nachschlagen koennte.
            retentionPercent: p.data?.metadata?.globalData?.retention?.percent,
        };

        // Unterprojekte und Projekte ohne Kunde fallen ohnehin heraus — ihnen
        // einen Projektcheck zu spendieren waere verschwendete Ladezeit.
        if (!base.isChild && base.customerId) {
            try {
                const input = await buildProjectCheckInput(p.id, {
                    documentType: params.documentType,
                    periodFrom: params.periodFrom,
                    periodTo: params.periodTo,
                });
                if (input) {
                    base.check = runProjectCheck(input, today, checkConfig);
                    base.reservations = input.reservations;
                }
                // Die Belege tragen, welche Protokolleintraege bereits
                // abgerechnet sind. Ohne sie kappt der Lauf den Zeitraum auf
                // die volle dort protokollierte Menge und boete einen
                // abgerechneten Monat bei jedem weiteren Lauf erneut an.
                const projectInvoices = await fetchProjectInvoices(p.id);
                base.invoices = projectInvoices;
                // Externe Alt-Abschlaege — nur fuer die Deckungsfrage unten.
                // Eigener catch: ein Fehlschlag hier darf weder das Projekt aus
                // dem Lauf werfen (das taete der aeussere catch) noch zum Zwang
                // fuehren. `null` heisst „nicht ermittelt", und dann wird nicht
                // gezwungen.
                const externalAdvances = await fetchExternalAdvances(p.id).catch((e) => {
                    console.error(
                        `Externe Alt-Abschläge für Projekt ${p.id} (${p.name}) konnten nicht geladen werden; die fortschreibende Anlage wird nicht erzwungen.`,
                        e,
                    );
                    return null;
                });
                // Das Abrechnungsmodell FOLGT DEM PROJEKT. Massgeblich ist der
                // juengste ausgestellte Beleg, der ueberhaupt eine Aussage
                // trifft: an den muss die naechste Rechnung anschliessen.
                // Welche Belege das sind, steht in billingMode.ts — dieselbe
                // Ableitung wie in der Belegliste, damit die Ansicht nicht ein
                // anderes Modell anzeigt als der Lauf verwendet.
                //
                // Ohne Aussage entscheidet nicht mehr allein die Voreinstellung
                // des Laufs: ein Haken „Kumulativ" gilt hier fuer den GANZEN
                // Stapel, und ueber 150 Bestandsprojekte tragen Altfakturierung
                // ohne Beleg. Ein einziger Lauf ueber vierzig Kunden erzeugte
                // sonst vierzig Belege der Bauart 6172 — dieselbe Schadensklasse
                // wie im Anlagedialog, nur in einem Zug. Dieselbe Rechnung wie
                // dort und wie die Sperre beim Ausstellen.
                base.cumulative = resolveBillingMode(
                    params.cumulative === true,
                    establishedBillingMode(projectInvoices),
                    projectUncoveredPriorBilling({
                        services: base.services,
                        serviceLog: base.serviceLog,
                        invoicedQtyByService: invoicedQuantityByService(projectInvoices),
                        externalAdvances,
                        // Derselbe Stichtag, den der Lauf dem kumulativen Beleg
                        // gibt (deliveryPeriodForGroup: params.periodTo). Ohne
                        // gewaehlten Zeitraum faellt jener auf den letzten
                        // protokollierten Tag zurueck, hier auf "kein Stichtag"
                        // — also auf den vollen LV-Stand. Beides umfasst
                        // dasselbe Protokoll; die Auswahl kann dadurch
                        // hoechstens eine Position mehr enthalten, nie eine
                        // weniger. Der Zwang wird davon eher strenger.
                        asOf: params.periodTo || undefined,
                    }) != null,
                ).cumulative;
            } catch (e) {
                // Ohne Check bleibt das Projekt ungeprueft und wird vom
                // Trockenlauf mit Begruendung ausgeschlossen. Ein Fehler hier
                // darf den ganzen Lauf nicht zum Erliegen bringen — er darf aber
                // auch nicht spurlos verschwinden: faellt der Check fuer ALLE
                // Projekte aus (kaputte Sicht, Rechte-Regression), saehe der
                // Bericht sonst aus wie ein Datenqualitaetsproblem statt wie
                // ein Systemfehler.
                console.error(
                    `Projektcheck für Projekt ${p.id} (${p.name}) fehlgeschlagen; das Projekt wird als ungeprüft ausgeschlossen.`,
                    e,
                );
                base.check = undefined;
            }
        }

        out.push(base);
        done += 1;
        onProgress?.(done, selected.length);
    }
    return out;
};

export interface BillingRunFailure {
    customerId: string;
    customerName?: string;
    /** Welche Projekte in dieser Gruppe steckten — sonst muss der Nutzer suchen. */
    projectIds: string[];
    projectNames: string[];
    message: string;
}

/**
 * Vermerk zu einer angelegten Gruppe.
 *
 * Dieselbe Struktur wie ein Fehlschlag, weil dieselbe Frage dahintersteht — "um
 * welche Gruppe geht es?" —, aber mit dem umgekehrten Ausgang: der Entwurf ist
 * entstanden, er braucht nur noch eine Hand. Ihn unter die Fehlschlaege zu
 * mischen liesse den Lauf schlechter aussehen, als er gelaufen ist.
 */
export type BillingRunNote = BillingRunFailure;

export interface ExecuteResult {
    run: BillingRun | null;
    createdInvoiceIds: string[];
    failures: BillingRunFailure[];
    /** Angelegt, aber mit Nacharbeit — siehe BillingRunNote. */
    notes: BillingRunNote[];
    /** Gesetzt, wenn die Entwuerfe entstanden sind, das Laufprotokoll aber nicht. */
    runLogError?: string;
}

/**
 * Legt die Entwuerfe des Laufs an.
 *
 * Ein Fehler in einer Gruppe bricht den Lauf NICHT ab. Ein Stapel, der bei
 * Gruppe 7 von 40 stirbt, ist schlimmer als ein Teilstapel: die
 * Reservierungslogik macht die Wiederholung gefahrlos, denn beim zweiten Lauf
 * sind die bereits angelegten Entwuerfe reserviert und tauchen nicht erneut auf.
 *
 * Sequenziell, nie Promise.all — jede Anlage schreibt in dieselbe Tabelle, und
 * die Reservierungssicht muss zwischen zwei Gruppen aktuell sein.
 */
export const executeBillingRun = async (
    preview: BillingRunPreview,
    candidates: BillingCandidate[],
    params: BillingRunParams,
    cfg: InvoiceConfig,
    todayIso: string,
): Promise<ExecuteResult> => {
    const { data: runRow, error: runErr } = await supabase
        .from('billing_runs')
        .insert({
            status: 'PREVIEW',
            params,
            report: {
                excluded: preview.excluded,
                totals: preview.totals,
            },
        })
        .select('*')
        .single();
    if (runErr) throw runErr;

    const createdInvoiceIds: string[] = [];
    const failures: ExecuteResult['failures'] = [];
    const notes: ExecuteResult['notes'] = [];

    // Zahlungsziel und Skonto aller beteiligten Kunden in EINER Abfrage. Die
    // Entwuerfe entstehen sequenziell; holte sich jeder seine Konditionen
    // selbst, kaeme ein zusaetzlicher serieller Roundtrip je Gruppe hinzu.
    // Wer hier fehlt, bekommt in createInvoiceDraft weiterhin die
    // Einzelabfrage — der Lauf soll daran nicht scheitern.
    const customerTerms = await fetchCustomerTerms(preview.groups.map((g) => g.customerId));

    // Ebenso die Vorbelegungen aus den letzten Belegen — Kostenstelle und
    // abweichender Projektname: EINE Abfrage ueber alle beteiligten Projekte
    // vor der Schleife, dann je Gruppe die passende durchgereicht. Holte sich
    // jeder Entwurf seine selbst, kaeme bei vierzig Gruppen ein vierzigfacher
    // Roundtrip zusammen.
    //
    // Je PROJEKT und nicht eine fuer den ganzen Lauf: ein Stapel geht quer
    // durch die Baustellen, und beide Angaben gehoeren dem Auftraggeber der
    // einzelnen. Eine gemeinsame Vorbelegung traege sie in fremde Belege.
    //
    // `null` heisst beim Durchreichen "nachgeschlagen, es gibt keine" und ist
    // genau deshalb wichtig — es unterscheidet sich von "nicht nachgeschlagen"
    // und haelt die Einzelabfrage im Entwurf zurueck. Fehlschlaege faengt die
    // Funktion selbst ab; ein Abrechnungslauf scheitert nicht an einer
    // Bequemlichkeit.
    //
    // Gefragt wird nur fuer Gruppen mit GENAU EINEM Projekt: nur die bekommen
    // ein primaeres Projekt an den Beleg, und nur daran haengt die Vorbelegung.
    //
    // Der Debitor der Gruppe geht mit: die abweichende Rechnungsanschrift darf
    // nur aus Belegen DESSELBEN Kunden kommen. Hier faellt das besonders ins
    // Gewicht — ein Lauf legt die Entwuerfe fuer alle Projekte auf einmal an,
    // und ein umgehaengtes Projekt schriebe die Anschrift seines frueheren
    // Auftraggebers gleich reihenweise auf fremde Forderungen.
    const lastDefaults = await fetchLastUsedInvoiceDefaults(
        preview.groups
            .filter((g) => g.projects.length === 1)
            .map((g) => ({ projectId: g.projects[0].projectId, customerId: g.customerId })),
    );
    /** Die Vorbelegungen dieser Gruppe — ohne eindeutiges Projekt: keine. */
    const lastDefaultsFor = (projectId?: string): LastUsedInvoiceDefaults | null =>
        (projectId ? lastDefaults.get(projectId) : null) ?? null;

    // Der Zeitraum, den die Belege des Laufs AUSWEISEN — volle Monatsgrenzen
    // des gewaehlten Laufzeitraums, fuer alle Gruppen derselbe. Bewusst aus
    // params und nicht aus deliveryPeriodForGroup: jener Zeitraum steuert die
    // Auswahl und wird kumulativ bis zur ersten Leistung des Projekts
    // zurueckgezogen. Ohne gewaehlten Zeitraum bleibt er leer, und der Andruck
    // faellt wie bisher auf das Leistungsdatum zurueck.
    const servicePeriod = servicePeriodFromSelection({
        from: params.periodFrom,
        to: params.periodTo,
    });

    for (const group of preview.groups) {
        try {
            // Ein Beleg traegt EINEN Sicherheitseinbehalt. Tragen die Projekte
            // dieser Gruppe verschiedene Saetze, bekaeme mindestens einer der
            // Auftraege den falschen — dann entsteht der Beleg lieber nicht.
            // Als Fehlschlag und nicht als blosser Vermerk: es gibt nichts
            // nachzutragen, der Lauf muss anders geschnitten werden.
            //
            // Optional gelesen, und das ist tragend: `retention` setzt
            // `previewBillingRun`, aber nicht jeder Aufrufer kommt von dort —
            // eine von Hand gebaute Gruppe (Tests, kuenftige Aufrufer) haette
            // das Feld sonst nicht, und ein Zugriff darauf liesse nicht diesen
            // einen Beleg, sondern den GANZEN Lauf ohne einen einzigen Beleg
            // enden. Fehlt die Angabe, gilt "kein Einbehalt" — der Stand vor
            // diesem Vorhaben.
            if (group.retention?.conflict) {
                throw new Error(group.retention.conflict);
            }
            const period = deliveryPeriodForGroup(group, candidates, params);

            // ── Kumulativ: derselbe Weg wie bei der einzelnen Rechnung ──────
            //
            // createInvoiceDraft kennt weder das Abrechnungsmodell noch die
            // Abzuege. Ein Lauf ueber diesen Weg erzeugte fortschreibende
            // Belege ohne jeden Abzug — in einem kumulativ abrechnenden Betrieb
            // Belege einer anderen Bauart als alles uebrige.
            //
            // Die Positionen werden dafuer neu gebaut: die Vorschau zeigt die
            // OFFENE Menge, also den Betrag, den der Beleg fordert; ein
            // kumulativer Beleg nennt aber den Gesamtstand. Beides ist richtig,
            // es ist nur nicht dieselbe Zeile.
            if (group.cumulative && group.projects.length === 1) {
                const projectId = group.projects[0].projectId;
                const built = await buildProjectServiceLines(projectId, {
                    periodTo: period.to,
                    cumulative: true,
                    // Die Mengen ohne Datum wie in der Vorschau. Dort rechnet
                    // previewBillingRun fuer kumulative Projekte dieselbe
                    // offene Menge wie fuer alle (computeOpenPositions mit
                    // params.includeUndated); fehlte die Option hier, zeigte
                    // die Vorschau mit Haken mehr, als der Beleg fordert.
                    //
                    // Ohne gewaehlten Zeitraum zaehlt sie als gesetzt: die
                    // Vorschau nimmt dann ohne jedes Fenster ALLES Offene
                    // (der Assistent blendet den Haken aus — „ohnehin alles
                    // dabei"), der kumulative Aufbau setzt aber trotzdem einen
                    // Stichtag (den letzten protokollierten Tag, siehe
                    // deliveryPeriodForGroup) und liesse ohne sie die Mengen
                    // ohne Datum liegen.
                    includeUndated:
                        params.includeUndated === true || !(params.periodFrom || params.periodTo),
                    taxRateBp: cfg.defaultTaxRateBp,
                });
                // Der Leistungsbeginn kommt aus derselben Quelle wie die
                // Positionen — dem frisch geladenen Protokoll. period.from
                // traegt dieselbe Ableitung aus den Kandidatendaten der
                // Vorschau und bleibt der Rueckfall.
                const deliveryDateFrom = built.cumulativeFrom ?? period.from;
                const { invoice: cumulativeInvoice } = await createFinalInvoice(
                    {
                        customerId: group.customerId,
                        primaryProjectId: projectId,
                        lines: built.lines,
                        documentType:
                            params.documentType === 'FINAL_INVOICE' ? 'FINAL_INVOICE' : 'INVOICE',
                        invoiceDate: params.invoiceDate,
                        deliveryDateFrom,
                        deliveryDateTo: period.to,
                        cumulative: true,
                        details: {
                            ...servicePeriod,
                            ...performedDetails(built.performed),
                            // Die Wahl, mit der GENAU diese Zeilen gebaut
                            // wurden (details.includeUndated) — aus dem
                            // Ergebnis, also einschliesslich „ohne Zeitraum
                            // zaehlt als mit" von oben.
                            includeUndated: built.includeUndated,
                            // Nur wenn vereinbart: eine ausdrueckliche 0 am
                            // Beleg schluege die Projekt-Vorbelegung, und bei
                            // "nichts vereinbart" ist genau das nicht gemeint.
                            ...((group.retention?.percent ?? 0) > 0
                                ? { retentionPercent: group.retention?.percent }
                                : {}),
                        },
                        customerTerms: customerTerms.get(group.customerId),
                        lastDefaults: lastDefaultsFor(projectId),
                    },
                    cfg,
                    todayIso,
                );
                createdInvoiceIds.push(cumulativeInvoice.id);
                // Kein Protokolleintrag mit Datum und kein gewaehlter Monat: der
                // Entwurf steht ohne Leistungszeitraum da. Das jetzt sagen und
                // nicht erst die Ausstellung — dort trifft es den Bearbeiter
                // einzeln und Tage spaeter.
                const note = deliveryPeriodNote({ from: deliveryDateFrom, to: period.to });
                if (note) {
                    notes.push({
                        customerId: group.customerId,
                        customerName: group.customerName,
                        projectIds: group.projects.map((p) => p.projectId),
                        projectNames: group.projects.map((p) => p.projectName),
                        message: note,
                    });
                }
                continue;
            }

            // Bei genau einem Projekt bleibt der Bezug erhalten; bei mehreren
            // gibt es kein "primaeres" Projekt, die Zuordnung steht dann in den
            // Zeilen — und ohne primaeres Projekt gibt es auch keine
            // Kostenstellen-Vorbelegung.
            const primaryProjectId =
                group.projects.length === 1 ? group.projects[0].projectId : undefined;
            const invoice = await createInvoiceDraft(
                {
                    customerId: group.customerId,
                    documentType: params.documentType,
                    primaryProjectId,
                    billingRunId: runRow.id,
                    invoiceDate: params.invoiceDate,
                    deliveryDateFrom: period.from,
                    deliveryDateTo: period.to,
                    lines: group.lines,
                    customerTerms: customerTerms.get(group.customerId),
                    lastDefaults: lastDefaultsFor(primaryProjectId),
                    details: {
                        ...servicePeriod,
                        ...((group.retention?.percent ?? 0) > 0
                            ? { retentionPercent: group.retention?.percent }
                            : {}),
                        // Die Zeilen der Gruppe hat der Trockenlauf mit
                        // DIESEN params gebaut (previewBillingRun:
                        // computeOpenPositions mit params.includeUndated) —
                        // die Wahl gehoert an den Beleg wie beim Einzelbeleg.
                        includeUndated: params.includeUndated === true,
                        billingRun: {
                            runId: runRow.id,
                            projectIds: group.projects.map((p) => p.projectId),
                            traffic: group.traffic,
                            findingCount: group.findingCount,
                        },
                    } as never,
                },
                cfg,
                todayIso,
            );
            createdInvoiceIds.push(invoice.id);
        } catch (e) {
            failures.push({
                customerId: group.customerId,
                customerName: group.customerName,
                projectIds: group.projects.map((p) => p.projectId),
                projectNames: group.projects.map((p) => p.projectName),
                message: e instanceof Error ? e.message : 'Unbekannter Fehler',
            });
        }
    }

    const { data: finished, error: finErr } = await supabase
        .from('billing_runs')
        .update({
            status: 'CREATED',
            created_invoice_ids: createdInvoiceIds,
            report: {
                excluded: preview.excluded,
                totals: preview.totals,
                failures,
                notes,
            },
        })
        .eq('id', runRow.id)
        .select('*')
        .single();

    // Hier NICHT werfen. Die Entwuerfe sind bereits angelegt; ein geworfener
    // Fehler wuerde createdInvoiceIds verschlucken, und die Oberflaeche meldete
    // einen Fehlschlag, obwohl in der Datenbank N Rechnungen liegen. Der Nutzer
    // wuerde den Lauf wiederholen und die Nachvollziehbarkeit verlieren.
    if (finErr) {
        console.error('Das Laufprotokoll konnte nicht fortgeschrieben werden.', finErr);
        return {
            run: null,
            createdInvoiceIds,
            failures,
            notes,
            runLogError: finErr.message ?? 'Das Laufprotokoll konnte nicht aktualisiert werden.',
        };
    }

    return { run: mapRunRow(finished), createdInvoiceIds, failures, notes };
};

export const fetchBillingRuns = async (limit = 20): Promise<BillingRun[]> => {
    const { data, error } = await supabase
        .from('billing_runs')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(limit);
    if (error) throw error;
    return (data || []).map(mapRunRow);
};

/**
 * Trockenlauf: Kandidaten sammeln und gruppieren, ohne etwas zu schreiben.
 *
 * Genau diese Funktion liefert auch den Beweis der Idempotenz — ein zweiter
 * Aufruf nach dem Anlegen der Entwuerfe muss ein leeres Ergebnis liefern, weil
 * die Mengen dann reserviert sind.
 *
 * `customerNames` geht unveraendert an gatherCandidates (Kundenname der
 * Gruppen).
 */
export const previewRun = async (
    params: BillingRunParams,
    cfg: InvoiceConfig,
    todayIso: string,
    onProgress?: (done: number, total: number) => void,
    customerNames?: ReadonlyMap<string, string>,
): Promise<{ preview: BillingRunPreview; candidates: BillingCandidate[] }> => {
    const candidates = await gatherCandidates(
        params,
        cfg.projectCheck,
        todayIso,
        onProgress,
        customerNames,
    );

    // Reservierungen fuer Kandidaten ohne Projektcheck nachziehen, damit auch
    // sie mit dem korrekten offenen Bestand bewertet werden.
    const missing = candidates.filter((c) => !c.reservations).map((c) => c.projectId);
    if (missing.length) {
        const map = await fetchReservations(missing);
        for (const c of candidates) {
            // Je Projekt die EIGENE Sicht — nicht dieselbe zusammengefuehrte
            // Karte an alle, sonst wirkten fremde Reservierungen mit.
            if (!c.reservations) c.reservations = reservationsForProject(map, c.projectId);
        }
    }

    return {
        preview: previewBillingRun(
            candidates,
            params,
            () => crypto.randomUUID(),
            cfg.defaultTaxRateBp,
        ),
        candidates,
    };
};
